// ─────────────────────────────────────────────────────────────────────────
// historyRateKeys — WHICH sub-key of an appendHistory entry is the amount,
// which is the currency and which is the unit.
//
// An appendHistory container stores entries like:
//   { candidateBudgetEnd: 0, candidateBudgetStart: 92,
//     candidateBudgetCurrency: 'USD', candidateBudgetUnit: 'Hourly',
//     candidateBudgetCurrencySymbol: '', timestamp, updatedBy }
//
// The detail view used to GUESS the amount with a regex over the entry's keys
// ("the first key matching rate|budget|amount that holds a number"). Object key
// order is insertion order, so on the entry above that guess returned
// `candidateBudgetEnd: 0` and the Rate Details card rendered "$0" for a rate
// the recruiter had actually saved as 92 — the QA bug this module fixes.
//
// The fix is not a better guess: the admin config ALREADY says which sub-key is
// which. The group that declares the container configures exactly the sub-fields
// that matter ("<container>.<subKey>"), so the amount is simply the first
// configured sub-field that is not a currency/symbol/unit/type key — and
// `candidateBudgetEnd`, which nobody configured, can never be picked.
//
// Resolution precedence, per slot (amount / currency / unit):
//   1. group.historyAmountFields[container]   explicit admin override
//      group.historyCurrencyFields[container]
//      group.historyUnitFields[container]
//   2. the group's configured "<container>.<subKey>" fields
//   3. the legacy regex heuristic over the entry's own keys (unchanged, so a
//      container whose group configures no sub-fields behaves exactly as before)
//
// Nothing here knows about submissions, candidateBudget, proposedRate or any
// other module/field name: every input is admin config plus one stored entry.
// ─────────────────────────────────────────────────────────────────────────

// Sub-keys that describe an amount rather than BEING one.
const META_KEY = /(currency|symbol|unit|type)/i;
// The legacy amount heuristic, kept verbatim as the fallback.
const AMOUNT_LIKE = /(rate|budget|amount)/i;

const isNumericish = (v) => typeof v === 'number'
  || (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)));

const truthyFlag = (value) => value === true || value === 1 || value === '1';

// A group "owns" a container when its appendHistory config declares it.
export function groupOwnsContainer(group, container) {
  if (!group || !container || !truthyFlag(group.appendHistory)) return false;
  return [group.historyField, ...(group.historyFields ?? [])].includes(container);
}

// Config may name a sub-key either bare (`candidateBudgetStart`) or fully
// qualified (`candidateBudget.candidateBudgetStart`) — the field keys in the
// group are written the qualified way, so admins reach for both. Both mean the
// same sub-key of the same container.
function subKeyOf(container, raw) {
  const value = String(raw ?? '').trim();
  if (!value) return '';
  const prefix = `${container}.`;
  return value.startsWith(prefix) ? value.slice(prefix.length) : value;
}

// The sub-keys this group configures under "<container>.", in config order.
// Order is what makes the inference work: the admin already put the field they
// consider "the rate" first, and the ones they don't care about (an End bound
// nobody fills in) are not in the list at all.
export function configuredSubKeys(container, groups = []) {
  const prefix = `${container}.`;
  const out = [];
  (groups ?? []).forEach((group) => {
    if (!groupOwnsContainer(group, container)) return;
    (group.fields ?? []).forEach((field) => {
      const key = String(field?.field ?? '');
      if (!key.startsWith(prefix)) return;
      const sub = key.slice(prefix.length);
      if (sub && !out.includes(sub)) out.push(sub);
    });
  });
  return out;
}

// The explicit override for one slot, from the first owning group that sets it.
function overrideFor(container, groups = [], mapKey) {
  for (const group of groups ?? []) {
    if (!groupOwnsContainer(group, container)) continue;
    const named = subKeyOf(container, group?.[mapKey]?.[container]);
    if (named) return named;
  }
  return '';
}

// Present means "the entry actually carries this key" — NOT "the value is
// truthy". A stored rate of 0 is a real value and must not send us back to the
// guess, and an empty currency symbol must not silently promote the code.
const present = (entry, key) => !!key && entry != null
  && Object.prototype.hasOwnProperty.call(entry, key) && entry[key] !== undefined;

// ── Legacy heuristic (fallback only) ────────────────────────────────────────
function fallbackAmountKey(entry) {
  const pair = Object.entries(entry ?? {}).find(([k, v]) => AMOUNT_LIKE.test(k)
    && !META_KEY.test(k) && isNumericish(v));
  return pair ? pair[0] : '';
}

function fallbackCurrencyKey(entry) {
  const keys = Object.keys(entry ?? {});
  return keys.find((k) => /currencysymbol/i.test(k) && String(entry[k] ?? '').trim() !== '')
    || keys.find((k) => /currency/i.test(k) && String(entry[k] ?? '').trim() !== '')
    || keys.find((k) => /symbol/i.test(k) && String(entry[k] ?? '').trim() !== '')
    || '';
}

function fallbackUnitKey(entry) {
  return Object.keys(entry ?? {}).find((k) => /unit/i.test(k)) || '';
}

// ── Public resolution ───────────────────────────────────────────────────────
// resolveHistoryRateKeys — the three keys to read out of ONE entry of
// `container`, given the admin's groups. Any slot can come back '' when nothing
// resolves it; the caller renders that slot as empty rather than inventing one.
//
// The entry participates in the decision only as a presence check: a configured
// key the stored entry never had (an old entry written before the field was
// added) falls through to the next source instead of rendering blank.
export function resolveHistoryRateKeys(entry, container, groups = []) {
  const configured = configuredSubKeys(container, groups);

  // amount — override, else the first configured sub-key that is not metadata.
  const amountOverride = overrideFor(container, groups, 'historyAmountFields');
  const amountConfigured = configured.find((k) => !META_KEY.test(k));
  const amountKey = (present(entry, amountOverride) && amountOverride)
    || (present(entry, amountConfigured) && amountConfigured)
    || fallbackAmountKey(entry);

  // currency — override, else an explicit SYMBOL sub-key when the entry
  // actually carries a symbol (a configured-but-empty symbol key is useless,
  // so the code sub-key wins in that case), else the currency code.
  const currencyOverride = overrideFor(container, groups, 'historyCurrencyFields');
  const symbolConfigured = configured.find((k) => /symbol/i.test(k)
    && present(entry, k) && String(entry[k] ?? '').trim() !== '');
  const codeConfigured = configured.find((k) => /currency/i.test(k) && !/symbol/i.test(k));
  const currencyKey = (present(entry, currencyOverride) && currencyOverride)
    || symbolConfigured
    || (present(entry, codeConfigured) && codeConfigured)
    || fallbackCurrencyKey(entry);

  // unit — override, else the configured unit sub-key.
  const unitOverride = overrideFor(container, groups, 'historyUnitFields');
  const unitConfigured = configured.find((k) => /unit/i.test(k));
  const unitKey = (present(entry, unitOverride) && unitOverride)
    || (present(entry, unitConfigured) && unitConfigured)
    || fallbackUnitKey(entry);

  return { amountKey: amountKey || '', currencyKey: currencyKey || '', unitKey: unitKey || '' };
}

// readHistoryRateParts — the raw values behind those keys. Formatting (symbol,
// unit wording, thousands separators) is NOT done here: it belongs to
// src/utils/currency.js, which every money surface shares.
export function readHistoryRateParts(entry, container, groups = []) {
  const { amountKey, currencyKey, unitKey } = resolveHistoryRateKeys(entry, container, groups);
  return {
    amountKey,
    currencyKey,
    unitKey,
    amount: amountKey ? entry?.[amountKey] : undefined,
    currency: currencyKey ? entry?.[currencyKey] : '',
    unit: unitKey ? entry?.[unitKey] : '',
  };
}
